import { Injectable } from '@angular/core';

import { DateTime } from 'luxon';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { DateRange } from '@common/models';
import { DatesService } from '@common/services';
import { RangeKey } from '@common/enum';

import { PopupRangeStore } from './popup-range-store';
import { DateUtilities, PopupRangeParameters, PopupCalendarConfig } from './';

@Injectable()
export class PopupRangeService
{
    private PopupRangeStore: PopupRangeStore;
    private _PopupRangeStore = new BehaviorSubject<PopupRangeStore>({} as PopupRangeStore);
    public popupCalendarConfig: PopupCalendarConfig = new PopupCalendarConfig();
    private _popupCalendarConfig = new BehaviorSubject<PopupCalendarConfig>({} as PopupCalendarConfig);
    private lang: string;
    // #region Getter/Setter
    get minDate ()
    {
        return this.PopupRangeStore.minDate;
    }
    set minDate (value: DateTime)
    {
        this.PopupRangeStore.minDate = value;
        this.nextConfig();
    }
    get maxDate ()
    {
        return this.PopupRangeStore.maxDate;
    }
    set maxDate (value: DateTime)
    {
        this.PopupRangeStore.maxDate = value;
        this.nextConfig();
    }
    /**
     * Valeur courante de since
     */
    get currentSince ()
    {
        return this.PopupRangeStore.currentSince;
    }
    set currentSince (value: DateTime)
    {
        this.PopupRangeStore.currentSince = value;
        this.nextConfig();
    }
    /**
     * Valeur courante de until
     */
    get currentUntil ()
    {
        return this.PopupRangeStore.currentUntil;
    }
    set currentUntil (value: DateTime)
    {
        this.PopupRangeStore.currentUntil = value;
        this.nextConfig();
    }
    // get format ()
    // {
    //     return this.PopupRangeStore.format;
    // }
    // set format (value: string)
    // {
    //     this.PopupRangeStore.format = value;
    // }
    set language (value: string)
    {
        this.nextConfig();
    }
    get rangeConfig ():Observable<PopupRangeStore>
    {
        return this._PopupRangeStore.asObservable();
    }
    get dateRange (): DateRange
    {
        return new DateRange(this.PopupRangeStore.currentSince, this.PopupRangeStore.currentUntil, this.translateService.instant('NUMBERS.FORMATS.DATES.LUXON.FULL'));
    }
    get initRange (): DateRange
    {
        return new DateRange(this.PopupRangeStore.initSince, this.PopupRangeStore.initUntil,this.translateService.instant('NUMBERS.FORMATS.DATES.LUXON.FULL'));
    }
    set firstDaySinceCalendar (value: DateTime)
    {
        this.PopupRangeStore.firstDaySinceCalendar = value;
        this.nextConfig();
    }
    set firstDayUntilCalendar (value: DateTime)
    {
        this.PopupRangeStore.firstDayUntilCalendar = value;
        this.nextConfig();
    }
    public initConfig (value: PopupRangeParameters):void
    {
        this.PopupRangeStore = PopupRangeStore.init(value);
        this.language = this.translateService.currentLang;
        this.nextConfig();
    }
    // #endregion

    constructor (
        private translateService: TranslateService
    )
    {
    }

    ngOnDestroy (): void
    {
        // 'ngOnDestroy: cleaning up...'
    }

    public onDayClick (isSinceCalendar: boolean, date: DateTime): void
    {
        if (isSinceCalendar)
        {
            if (DateUtilities.isStrictlyAfter(date, this.PopupRangeStore.currentUntil))
            {
                const temp: DateTime = this.PopupRangeStore.currentUntil;

                this.currentUntil = date;
                this.currentSince = temp.startOf('day');
            }
            else
            {
                this.currentSince = date.startOf('day');
            }
        }
        else
        {
            if (DateUtilities.isStrictlyBefore(date, this.PopupRangeStore.currentSince))
            {
                const temp: DateTime = this.PopupRangeStore.currentSince;

                this.currentSince = date;
                this.currentUntil = temp.endOf('day');
            }
            else
            {
                this.currentUntil = date.endOf('day');
            }
        }
    }

    public setHour (isSinceCalendar: boolean, hour: number)
    {
        if (isSinceCalendar)
        {
            this.currentSince = this.currentSince.set({ hour: hour });
        }
        else
        {
            this.currentUntil = this.currentUntil.set({ hour: hour });
        }
    }

    public setMinute (isSinceCalendar: boolean, minute: number)
    {
        if (isSinceCalendar)
        {
            this.currentSince = this.currentSince.set({ minute: minute });
        }
        else
        {
            this.currentUntil = this.currentUntil.set({ minute: minute });
        }
    }

    public setByRangeKey (rangeKey: string)
    {
        const { since, until } = DatesService.preRangeDatesStatic(RangeKey[rangeKey]);

        this.currentSince = (since < this.minDate ? this.minDate : since).startOf('day');
        this.currentUntil = until > this.maxDate ? this.maxDate : until.endOf('day');
    }

    public resetRange ():void
    {
        this.currentSince = this.PopupRangeStore.initSince;
        this.currentUntil = this.PopupRangeStore.initUntil;
    }

    private nextConfig (): void
    {
        this._PopupRangeStore.next(Object.assign(new PopupRangeStore(), this.PopupRangeStore));
    }
}
